// ⌘K command palette.

import { Fragment, useEffect, useMemo, useState } from "react";
import { useAppData, useScreenEnabled } from "../store";
import type { Screen } from "../types";
import { Ic } from "../components/Icon";

interface Item {
  group: string;
  icon: string;
  title: string;
  sub?: string;
  kbd?: string;
  action: () => void;
}

interface Props {
  onClose: () => void;
  onNav: (s: Screen) => void;
  onOpenDeal: (id: string) => void;
}

export function Cmdk({ onClose, onNav, onOpenDeal }: Props) {
  const { data } = useAppData();
  const screenEnabled = useScreenEnabled();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const norm = q.toLowerCase().trim();

  const items: Item[] = useMemo(() => {
    if (!data) return [];
    const out: Item[] = [];
    const pool = [...data.deals, ...data.archive];
    pool.filter((d) =>
      !norm
      || d.co.toLowerCase().includes(norm)
      || d.founders.toLowerCase().includes(norm)
      || d.sector.toLowerCase().includes(norm),
    ).slice(0, 6).forEach((d) => out.push({
      group: "Deals", icon: "inbox", title: d.co,
      sub: `${d.founders} · ${d.sector} · ${d.score ?? "—"}`,
      action: () => onOpenDeal(d.id),
    }));

    const nav: [Screen, string][] = [
      ["inbox", "Inbox"], ["upload", "Upload"], ["archive", "Archive"],
      ["anti", "Anti-portfolio"], ["sim", "Simulator"], ["knowledge", "Knowledge"],
      ["routes", "Settings · Routes"], ["fields", "Settings · Fields"],
      ["rules", "Settings · Rules"], ["lists", "Settings · Lists"],
      ["tags", "Settings · Tags"], ["enrichments", "Settings · Enrichments"],
      ["team", "Settings · Team"], ["integrations", "Settings · Integrations"],
    ];
    nav.forEach(([id, label]) => {
      if (!screenEnabled(id)) return;
      if (!norm || label.toLowerCase().includes(norm)) {
        out.push({ group: "Navigate", icon: "arrowR", title: label, action: () => onNav(id) });
      }
    });

    return out;
  }, [data, norm, onNav, onOpenDeal, screenEnabled]);

  useEffect(() => setSel(0), [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 1)); }
      else if (e.key === "ArrowUp")   { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
      else if (e.key === "Enter")     { e.preventDefault(); items[sel]?.action(); onClose(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [items, sel, onClose]);

  const groups: Record<string, (Item & { idx: number })[]> = {};
  items.forEach((it, i) => {
    (groups[it.group] = groups[it.group] || []).push({ ...it, idx: i });
  });

  return (
    <div className="cmdk-back" onClick={onClose}>
      <div className="cmdk" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="search">
          <Ic name="search" size={16} />
          <input
            autoFocus placeholder="Search deals, actions, or jump to…"
            value={q} onChange={(e) => setQ(e.target.value)}
          />
          <span style={{ color: "var(--ink-3)", fontSize: 11 }}>{items.length} results</span>
        </div>
        <div className="list">
          {Object.entries(groups).map(([g, arr]) => (
            <Fragment key={g}>
              <div className="group-label">{g}</div>
              {arr.map((it) => (
                <div
                  key={it.idx}
                  className={"item" + (it.idx === sel ? " on" : "")}
                  onMouseEnter={() => setSel(it.idx)}
                  onClick={() => { it.action(); onClose(); }}
                >
                  <span className="ico"><Ic name={it.icon} /></span>
                  <span className="ttl">
                    {it.title}
                    {it.sub && <small>{it.sub}</small>}
                  </span>
                  {it.kbd && <span className="kbd">{it.kbd}</span>}
                </div>
              ))}
            </Fragment>
          ))}
          {items.length === 0 && (
            <div style={{
              padding: "30px 14px", textAlign: "center",
              color: "var(--ink-3)", fontSize: 12,
            }}>No matches</div>
          )}
        </div>
        <div className="foot">
          <span><span className="kbd">↑↓</span> navigate</span>
          <span><span className="kbd">↵</span> open</span>
          <span className="grow" />
          <span><span className="kbd">esc</span> close</span>
        </div>
      </div>
    </div>
  );
}
